import React, { useCallback, useEffect, useMemo, useState } from "react";
import PhoneInput, {
  Country,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  Value as PhoneInputValueType,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { Button } from "@shopflo/ui/button";
import { Icon } from "@shopflo/ui/icon";
import { PencilSimpleLine } from "@phosphor-icons/react";
import OTPInput from "components/auth/OTPInput";
import { CaptchaWrapper, CaptchaState, CaptchaStatus } from "components/common/CaptchaWrapper";
import TermsConditions from "components/common/TermsConditions";
import { useAuthContext } from "lib/contexts/AuthProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useUserContext } from "lib/contexts/UserProvider";
import { OTPChannel, OTPVerificationDetails } from "lib/types/user";
import { OTPDeliveryType, resentOtp, sendOtpWithPhoneNumber, verifyOtp } from "lib/utils/auth";
import { classNames, formatOtpResendClock, getPhoneInputMaxLength, setPhoneNumberSpacing } from "lib/utils/helpers";
import { bannerErrorToastV2 } from "lib/utils/toasters";
import { useLocale } from "lib/hooks/useLocale";

const ProfileLoginPrompt = () => {
  const { t } = useLocale();
  const {
    actions: { login },
  } = useAuthContext();
  const {
    state: { merchant, accountId },
  } = useMerchantContext();
  const {
    state: { user },
    actions: { updateUser },
  } = useUserContext();

  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phoneNumber, setPhoneNumber] = useState<PhoneInputValueType | undefined>(undefined);
  const [otp, setOtp] = useState("");
  const [contextId, setContextId] = useState("");
  const [counter, setCounter] = useState(-1);
  const [counterExpired, setCounterExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [phoneInputFocused, setPhoneInputFocused] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<Country | undefined>("IN");
  const [captchaToken, setCaptchaToken] = useState<CaptchaState>({
    token: null,
    status: CaptchaStatus.LOADING,
  });

  useEffect(() => {
    if (user?.phone) {
      setPhoneNumber(user.phone);
    }
  }, [user?.phone]);

  useEffect(() => {
    if (counter === 0) setCounterExpired(true);
    if (counter <= 0) return;
    const timer = setTimeout(() => setCounter(counter - 1), 1000);
    return () => clearTimeout(timer);
  }, [counter]);

  const isCaptchaValidating = merchant?.automationEnabled
    ? false
    : captchaToken.status === CaptchaStatus.LOADING || !Boolean(captchaToken.token);

  const formattedPhone = useMemo(() => setPhoneNumberSpacing(phoneNumber || ""), [phoneNumber]);

  const resetOtpState = () => {
    setOtp("");
    setOtpError("");
    setCounter(-1);
    setCounterExpired(false);
    setContextId("");
  };

  const handleBackToPhone = () => {
    setStep("phone");
    resetOtpState();
  };

  const validatePhoneNumber = () => {
    const value = phoneNumber || "";
    if (!value || !isPossiblePhoneNumber(value) || !isValidPhoneNumber(value)) {
      setPhoneError(t("valid_phone_number"));
      return false;
    }
    setPhoneError("");
    return true;
  };

  const handleSendOtp = async () => {
    if (!validatePhoneNumber()) return;

    try {
      setIsLoading(true);
      setOtpError("");

      const verificationDetails: OTPVerificationDetails = {
        identifier: phoneNumber || "",
        userId: user?.uid,
        merchantId: merchant?.merchantId || accountId || "",
      };
      const metadata: OTPDeliveryType = {
        context: "PROFILE",
      };

      const result = await sendOtpWithPhoneNumber(
        verificationDetails,
        metadata,
        captchaToken.token ?? "",
        merchant?.captchaProvider ?? "TURNSTILE",
      );

      // otp_required=false means the user is verified without OTP - log in directly with the returned token
      if (result?.otp_required === false && result?.token?.access_token) {
        login(
          result.token.access_token,
          result.token.refresh_token,
          result.token.access_token_expires_at,
          result.token.refresh_token_expires_at,
        );
        updateUser();
        return;
      }

      if (!result?.context_id) {
        setPhoneError(t("otp_fetch_failed"));
        return;
      }

      setContextId(result.context_id);
      setCounter(30);
      setCounterExpired(false);
      setStep("otp");
    } catch {
      setPhoneError(t("otp_fetch_failed"));
      setCaptchaToken((prev) => ({ ...prev, token: null }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = useCallback(async () => {
    if (otp.length !== 4 || !contextId) return;

    try {
      setIsLoading(true);
      setOtpError("");

      const result = await verifyOtp(contextId, otp);
      login(
        result?.access_token,
        result?.refresh_token,
        result?.access_token_expires_at,
        result?.refresh_token_expires_at,
      );
      updateUser();
    } catch {
      setOtpError(t("otp_incorrect_login_sso"));
    } finally {
      setIsLoading(false);
    }
  }, [otp, contextId, login, updateUser, t]);

  const handleResendOtp = async () => {
    if (!contextId) return;

    try {
      setIsLoading(true);
      setOtp("");
      setOtpError("");
      await resentOtp(contextId, "SMS" as OTPChannel);
      setCounter(30);
      setCounterExpired(false);
    } catch {
      bannerErrorToastV2(t("otp_fetch_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (otp.length === 4) {
      void handleVerifyOtp();
    }
  }, [otp, handleVerifyOtp]);

  return (
    <div className="w-full bg-white sm:max-w-[440px] sm:overflow-hidden sm:rounded-[20px] sm:border sm:border-zinc-200 sm:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.50),0_0_0_1px_rgba(10,13,18,0.05),0_1px_2px_0_rgba(10,13,18,0.05)]">
      <div className="flex flex-col gap-4 border-b border-zinc-100 bg-[linear-gradient(125deg,rgba(244,244,245,0.00)_29.25%,rgba(244,244,245,0.50)_88.13%)] px-6 py-8 sm:px-10">
        <img
          src="https://d2s4qtdvyggozf.cloudfront.net/c0d37436-5f52-43fe-9496-5800a87468b1-1774345705image.png"
          alt={t("profile_brand_logo_alt")}
          className="h-5 w-fit"
        />
        <p className="text-text-md-medium leading-7 text-zinc-500">{t("profile_sign_in_subtitle")}</p>
      </div>

      <div className="flex flex-col gap-3 px-6 py-6 sm:px-6">
        {step === "phone" ? (
          <>
            <div className="flex flex-col gap-3">
              <PhoneInput
                international
                defaultCountry="IN"
                countryCallingCodeEditable={false}
                limitMaxLength
                smartCaret={false}
                disabled={isLoading}
                countries={merchant?.addressConfig?.enabledCountries}
                value={phoneNumber}
                onChange={(value) => {
                  setPhoneNumber(value);
                  if (phoneError) setPhoneError("");
                }}
                onCountryChange={(code) => setSelectedCountryCode(code)}
                numberInputProps={{
                  maxLength: getPhoneInputMaxLength(phoneNumber, selectedCountryCode),
                }}
                onFocus={() => setPhoneInputFocused(true)}
                onBlur={() => setPhoneInputFocused(false)}
                placeholder={t("profile_phone_placeholder")}
                className={classNames(
                  "placeholder-sm w-full appearance-none rounded-xl border border-zinc-200 bg-zinc-100 px-4 text-base text-zinc-900 placeholder:text-zinc-500 [&_.PhoneInputCountry]:mr-3 [&_.PhoneInputCountry]:border-r [&_.PhoneInputCountry]:border-zinc-200 [&_.PhoneInputCountry]:pr-3 [&_.PhoneInputCountryCallingCode]:text-zinc-500 [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:placeholder:text-zinc-500",
                  phoneInputFocused
                    ? "border-indigo-600 bg-white shadow-[0_0_0_2px_rgba(85,104,254,0.15)]"
                    : "",
                  phoneError ? "border-red-600" : "",
                  "h-11 sm:h-11",
                )}
              />
            </div>

            <Button
              variant="primary"
              size="lg"
              className="h-11 w-full rounded-2xl text-base shadow-[0_1px_2px_rgba(16,24,40,0.08),0_1px_3px_rgba(16,24,40,0.1)]"
              onClick={handleSendOtp}
              disabled={isLoading || isCaptchaValidating}>
              {isLoading ? t("profile_send_otp_loading") : t("continue")}
            </Button>
            <p className={classNames("text-center text-text-xs-regular leading-[18px]", phoneError ? "text-red-600" : "text-zinc-500")}>
              {phoneError || t("profile_otp_send_hint")}
            </p>
            <CaptchaWrapper context="PROFILE_LOGIN" onTokenChange={setCaptchaToken} status={captchaToken.status} />

            <div className="pt-2">
              <TermsConditions showSecuredLogo={false} />
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center gap-1 pb-5 text-center text-text-sm-medium text-zinc-500">
              <span>{t("shopify_login.otp_sent_to")}</span>
              <span className="underline decoration-zinc-300 underline-offset-2">{formattedPhone}</span>
              <button
                type="button"
                onClick={handleBackToPhone}
                aria-label={t("change")}
                className="cursor-pointer text-zinc-500 transition-colors hover:text-zinc-600">
                <Icon IconComponent={PencilSimpleLine} size="sm" />
              </button>
            </div>

            <OTPInput
              OTPLength={4}
              disabled={isLoading}
              value={otp}
              onChange={(value) => {
                setOtp(value);
                if (otpError) setOtpError("");
              }}
              hasError={Boolean(otpError)}
              autoFocus={true}
              inputType="tel"
              disableTheme={true}
              inputClassName="!h-11 !w-full !rounded-[10px] !border-transparent !bg-white !px-[10px] !text-base !font-medium !text-coal-light !shadow-[0_0_0_1px_rgba(106,114,130,0.24),0_1px_3px_0_rgba(26,26,26,0.08)] focus:!w-full focus:!border-transparent focus:!text-coal-light focus:!shadow-[0_0_0_1.5px_#4A5565,0_1.5px_4px_0_rgba(9,9,11,0.12)] focus:!ring-0"
            />

            <div className="py-5">
              {otpError ? <p className="text-center text-text-sm-regular text-red-600 mb-3">{otpError}</p> : null}
              <div className="flex items-center justify-center gap-1 text-text-sm-regular">
                {counterExpired ? (
                  <>
                    <span className="text-zinc-500">{t("shopify_login.not_received")}</span>
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="cursor-pointer text-indigo-600 transition-opacity hover:opacity-80"
                      disabled={isLoading}>
                      {t("resend_otp")}
                    </button>
                  </>

                ) : counter > 0 ? (
                  <span className="text-zinc-500">
                    {t("profile_resend_otp_in", {
                      time: formatOtpResendClock(counter),
                    })}
                  </span>
                ) : null}
              </div>
            </div>

            <Button
              variant="primary"
              size="lg"
              className="h-11 w-full rounded-lg text-text-sm-semibold shadow-[0_4px_8px_0_rgba(0,0,0,0.05),0_3px_6px_-3px_rgba(0,0,0,0.2),0_1px_0_0_rgba(0,0,0,0.15),inset_0_2px_0_0_rgba(255,255,255,0.3),inset_0_0.5px_0_0_rgba(0,0,0,0.25),inset_0_-1px_0_0_rgba(0,0,0,0.25)]"
              onClick={handleVerifyOtp}
              disabled={isLoading || otp.length !== 4}>
              {isLoading ? t("profile_verify_otp_loading") : t("continue")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileLoginPrompt;
